import React, { useCallback } from 'react';
import styled from 'styled-components';
import { ConstructionCaseContent } from '@remodeling/content';
import FlagVr from '../FlagVr';
import TrackingService from '@/services/tracking';
import { Box, HStack, Profile, Text, VStack } from '@design-system/pc';
import LoadImage from '@/components/LoadImage';

interface CardPropsType {
  item: ConstructionCaseContent;
  imgSize?: {
    width: string;
    height: string;
  };
}

const ConstructionCaseCard = ({ item, imgSize }: CardPropsType) => {
  const { id, apartment, detailUrl, title, shop, imageUrl, vrUrl } = item;

  const onClickCard = useCallback(() => {
    window.open(detailUrl);
  }, [detailUrl]);

  const onClickVr = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      window.open(vrUrl);
    },
    [vrUrl],
  );

  return item ? (
    <CardWrapper
      onClick={onClickCard}
      {...TrackingService.tagGTM('constructioncase_item_button', {
        'content-id': id,
      })}
    >
      <ImageWrap height={imgSize?.height || '250px'}>
        {vrUrl && (
          <FlagWrap
            onClick={onClickVr}
            {...TrackingService.tagGTM('constructioncase_vr_item_button')}
          >
            <FlagVr variant="imgBG" />
          </FlagWrap>
        )}
        <Box
          width={imgSize?.width || '100%'}
          height={imgSize?.height || '100%'}
        >
          <LoadImage src={imageUrl} alt={imageUrl} objectFit="cover" />
        </Box>
      </ImageWrap>

      <TextArea>
        <TagWrap>
          <TagText variant="body2" color="color_chip.gray.600">
            {apartment?.name?.substring(0, 6)}
          </TagText>
          <Divider />
          <TagText variant="body2" color="color_chip.gray.600">
            {apartment?.area}㎡
          </TagText>
          <Divider />
          <TagText variant="body2" color="color_chip.gray.600">
            {apartment?.sidoGungu}
          </TagText>
        </TagWrap>

        <TitleText variant="body1" color="color_chip.primary">
          {`${title.substring(0, 50)}`}
        </TitleText>

        <ShopInfoWrap space={4} alignment="center">
          <Profile size="xxxs" src={shop.image} fit="cover" />
          <TagText variant="body2" color="color_chip.gray.600">
            {shop?.name}
          </TagText>
        </ShopInfoWrap>
      </TextArea>
    </CardWrapper>
  ) : (
    <></>
  );
};

export default ConstructionCaseCard;

const Divider = () => {
  return (
    <Box
      width="1px"
      height="10px"
      margin="0 8px"
      backgroundColor="color_chip.gray.200"
      display="inline-block"
    />
  );
};

const CardWrapper = styled(VStack)`
  align-items: start;
  max-width: 376px;
  height: auto;
  cursor: pointer;
`;

const ImageWrap = styled(Box)<{ height: string }>`
  position: relative;
  border-radius: 2px;
  height: ${({ height }) => height};
`;

const FlagWrap = styled(Box)`
  position: absolute;
  z-index: 9;
  bottom: 12px;
  left: 12px;
`;

const TagWrap = styled(Box)`
  display: inline-block;
`;

const TitleText = styled(Text)`
  margin-top: 8px;
`;

const TagText = styled(Text)`
  display: inline-block;
`;

const ShopInfoWrap = styled(HStack)`
  gap: 4px;
  padding-top: 12px;
`;

const TextArea = styled(Box)`
  margin-top: 12px;
`;
